import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ExternalLink, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function Funnels() {
  const [funnels, setFunnels] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/funnels");
    setFunnels(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/funnels", {
        name,
        blocks: [
          { id: crypto.randomUUID(), type: "hero", props: { headline: name, subheadline: "Ship faster with Peak." } },
          { id: crypto.randomUUID(), type: "text", props: { text: "This is a landing page block. Edit me." } },
          { id: crypto.randomUUID(), type: "form", props: { title: "Get in touch", cta: "Send" } },
        ],
      });
      setCreating(false); setName("");
      navigate(`/funnels/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this funnel?")) return;
    try {
      await api.delete(`/funnels/${id}`);
      setFunnels((prev) => prev.filter((f) => f.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="funnels-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">Acquisition</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">Funnels</h1>
          <p className="text-[var(--peak-muted)] mt-1 text-sm">Simple landing pages that pipe leads straight into your CRM.</p>
        </div>
        <button data-testid="new-funnel-btn" onClick={() => setCreating(true)} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New funnel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {funnels.length === 0 && (
          <div className="col-span-full brutalist-card p-10 text-center">
            <div className="section-label">No funnels yet</div>
            <div className="font-heading font-bold text-2xl mt-1">Build your first landing page</div>
          </div>
        )}
        {funnels.map((f) => (
          <div key={f.id} className="brutalist-card p-6" data-testid={`funnel-card-${f.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className={`section-label ${f.published ? "text-[#008A27]" : ""}`}>{f.published ? "Published" : "Draft"}</div>
                <div className="font-heading font-bold text-2xl tracking-tight mt-1">{f.name}</div>
                <div className="text-xs text-[var(--peak-muted)] mt-1 font-mono">/p/{f.slug}</div>
              </div>
              <button onClick={() => remove(f.id)} data-testid={`delete-funnel-${f.id}`} className="hover:text-[#E50000]"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-[var(--peak-muted)] mt-3">{(f.blocks || []).length} blocks</div>
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <Link to={`/funnels/${f.id}`} data-testid={`edit-funnel-${f.id}`} className="bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-bold uppercase tracking-widest text-xs px-3 py-2">Edit</Link>
              {f.published && (
                <a href={`/p/${f.slug}`} target="_blank" rel="noreferrer" data-testid={`view-funnel-${f.id}`} className="border border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase tracking-widest text-xs px-3 py-2 inline-flex items-center gap-1">
                  View <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white border-2 border-[var(--peak-ink)] rounded-none" data-testid="new-funnel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tighter uppercase">New funnel</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="section-label">Name</label>
              <input required data-testid="funnel-name" className="brutalist-input" value={name} onChange={(e) => setName(e.target.value)} />
              <div className="section-label mt-1">We'll auto-generate a URL slug like <span className="font-mono">/p/your-name</span></div>
            </div>
            <DialogFooter className="flex gap-2 sm:justify-end">
              <button type="submit" data-testid="submit-funnel" className="px-6 py-2 bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest">Create</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
